// 操作実行関数 - シンプルに操作を実行して結果を返す

import type { DataTransformOperationUnion, DataSet, DerivedDataSet } from '../../types/dataset.types';
import { hasData } from '../../types/dataset.types';
import type { DataSchema, ColumnSchema, DataValue } from '../../types/core.types';
import type { DatasetId, ColumnId } from '../../types';
import { createColumnId, createColumnName } from '../../types';
import { ROW_ID_COLUMN_NAME } from '../../types/system-columns';
// NOTE: duckdbProxyはexecuteOperation（Public版）でのみ使用
// executeOperationWithoutLock（Worker内用）では使用しない（依存性注入パターン）
import { duckdbProxy } from './duckdb';
import { CrosstabEngine } from './crosstab/engine';
import { COUNT_FIELD_ID, type CrosstabConfiguration, type FieldDescriptor, type ValueFieldDescriptor, type AggregationFunction } from './crosstab/types';
import { FilterExpressionParser } from './FilterExpressionParser';
import { evaluateDerivedDataset } from './evaluateDataset';
import { useProjectStore } from '../stores/projectStore';
import logger from './logger';

export interface OperationResult {
  schema: DataSchema;
  data: { [columnId: string]: DataValue[] };
  rowCount: number;
  parentDatasetIds: DatasetId[];  // 操作実行時に抽出された参照データセットID
}

/**
 * SQL実行関数の型
 * メインスレッドではduckdbProxy.executeSQL、
 * Worker内ではDuckDBQueryEngine.executeSQLWithoutLockを渡す
 */
export type SqlExecutor = (
  query: string,
  targetDataset: DataSet,
  datasets: Record<string, DataSet>,
  existingSchema?: DataSchema
) => Promise<Omit<OperationResult, 'parentDatasetIds'>>;

/**
 * 操作を実行して結果を返す
 *
 * **処理フロー**：
 * - sql_query: duckdbProxy.executeSQL()を呼び出す（Worker経由）
 * - filter: フィルタ条件を満たす行をメモリ上で抽出する
 * - crosstab: クロス集計をメモリ上で実行する
 * - glm_diagnostics: GLM診断統計量をメモリ上で計算する
 *
 * **使用場面**：
 * - evaluateDerivedDataset（Public版）から呼ばれる場合
 * - SQL Editorからのクエリ実行（executeSQL経由）
 *
 * @param operation 実行する操作
 * @param datasets 利用可能なデータセットのマップ
 * @param parentDatasetId 親データセットID（operationに含まれていない場合に使用）
 * @param existingSchema 既存のスキーマ（列IDを保持するため）
 * @param derivedDataset 再評価対象のDerivedDataSet（exclusionsを適用するため）
 * @returns 実行結果
 */
export async function executeOperation(
  operation: DataTransformOperationUnion,
  datasets: Record<string, DataSet>,
  parentDatasetId?: string,
  existingSchema?: DataSchema,
  derivedDataset?: DerivedDataSet
): Promise<OperationResult> {
  switch (operation.type) {
    case 'sql_query': {
      // SQLクエリの実行（DuckDB-WASM）
      const targetDataset = datasets[operation.targetDatasetId];
      if (!targetDataset) {
        throw new Error(`Dataset ${operation.targetDatasetId} not found`);
      }

      // DuckDBで実行（全データセットを渡す）
      const result = await duckdbProxy.executeSQL(operation.query, targetDataset, datasets);

      // DerivedDataSetのexclusionsを適用
      if (derivedDataset?.exclusions?.excludedRows && Object.keys(derivedDataset.exclusions.excludedRows).length > 0) {
        logger.info('[executeOperation] Applying exclusions to SQL query result', {
          excludedRowCount: Object.keys(derivedDataset.exclusions.excludedRows).length
        });

        // Row #列を名前で検索
        const rowIdColumnDef = result.schema.columns.find(col => col.name === ROW_ID_COLUMN_NAME);
        if (!rowIdColumnDef) {
          logger.warn('[executeOperation] Row ID column not found in result schema, cannot apply exclusions', {
            availableColumns: result.schema.columns.map(c => c.name)
          });
          return result;
        }

        const rowIdData = result.data[rowIdColumnDef.id];
        if (!rowIdData) {
          logger.warn('[executeOperation] Row ID column data not found, cannot apply exclusions', {
            columnId: rowIdColumnDef.id,
            dataKeys: Object.keys(result.data)
          });
          return result;
        }

        // 除外対象のRowNumber値のセット
        const excludeRowIdsSet = new Set<number>(Object.keys(derivedDataset.exclusions.excludedRows).map(Number));

        // 除外対象でない行のインデックスを収集
        const validIndices: number[] = [];
        for (let i = 0; i < result.rowCount; i++) {
          const rowIdValue = rowIdData[i];
          if (typeof rowIdValue === 'number' && !excludeRowIdsSet.has(rowIdValue)) {
            validIndices.push(i);
          }
        }

        // フィルタ済みデータを構築
        const filteredData: Record<string, DataValue[]> = {};
        result.schema.columns.forEach(col => {
          const colData = result.data[col.id];
          if (colData) {
            filteredData[col.id] = validIndices.map(i => colData[i]);
          }
        });

        const excludedCount = result.rowCount - validIndices.length;
        logger.info('[executeOperation] Excluded rows from result', {
          originalRowCount: result.rowCount,
          excludedCount,
          remainingRows: validIndices.length
        });

        return {
          ...result,
          data: filteredData,
          rowCount: validIndices.length
        };
      }

      return result;
    }

    case 'crosstab': {
      // クロス集計の実行
      logger.info('[executeOperation] Executing crosstab operation', {
        targetDatasetId: operation.targetDatasetId,
        rowFields: operation.rowFields,
        columnFields: operation.columnFields
      });

      const targetDataset = datasets[operation.targetDatasetId];
      if (!targetDataset) {
        throw new Error(`Dataset ${operation.targetDatasetId} not found`);
      }

      // CrosstabConfigurationを構築
      const rowFieldDescriptors: FieldDescriptor[] = operation.rowFields.map((fieldName) => {
        const column = targetDataset.schema.columns.find(c => c.name === fieldName);
        if (!column) {
          throw new Error(`Row field ${fieldName} not found in dataset ${operation.targetDatasetId}`);
        }
        return {
          fieldId: column.id,
          fieldName: column.name,
          dataType: column.type
        };
      });

      const columnFieldDescriptors: FieldDescriptor[] = operation.columnFields.map((fieldName) => {
        const column = targetDataset.schema.columns.find(c => c.name === fieldName);
        if (!column) {
          throw new Error(`Column field ${fieldName} not found in dataset ${operation.targetDatasetId}`);
        }
        return {
          fieldId: column.id,
          fieldName: column.name,
          dataType: column.type
        };
      });

      const valueFieldDescriptors: ValueFieldDescriptor[] = operation.valueFields.map((vf) => {
        // Countのみの場合は特殊処理
        if (vf.field === COUNT_FIELD_ID) {
          return {
            fieldId: undefined,
            fieldName: '(Count)',
            aggregation: 'count' as AggregationFunction,
            dataType: 'int64',
            alias: vf.alias
          };
        }
        const valueColumn = targetDataset.schema.columns.find(c => c.name === vf.field);
        if (!valueColumn) {
          throw new Error(`Value field ${vf.field} not found in dataset ${operation.targetDatasetId}`);
        }
        return {
          fieldId: valueColumn.id,
          fieldName: valueColumn.name,
          aggregation: vf.aggregation as AggregationFunction,
          dataType: valueColumn.type,
          alias: vf.alias
        };
      });

      const config: CrosstabConfiguration = {
        rowFields: rowFieldDescriptors,
        columnFields: columnFieldDescriptors,
        valueFields: valueFieldDescriptors,
        options: {
          showRowTotals: operation.options.showRowTotals,
          showColumnTotals: operation.options.showColumnTotals,
          showGrandTotal: operation.options.showGrandTotal,
          emptyDisplayMode: 'blank',
          percentageMode: 'none',
          visualMode: 'table',
          colorScheme: 'blue'
        }
      };

      // Ensure dataset has data
      let datasetWithData;
      if (!hasData(targetDataset)) {
        if (targetDataset.type === 'derived') {
          datasetWithData = await evaluateDerivedDataset(targetDataset, datasets);
        } else {
          throw new Error('Dataset does not have data loaded');
        }
      } else {
        datasetWithData = targetDataset;
      }

      // CrosstabEngineで実行
      const engine = new CrosstabEngine();
      const crosstabResult = await engine.execute(datasetWithData, config);

      // ピボットテーブル形式に変換
      const { structure, data: crosstabData, configuration } = crosstabResult;
      const { rowHeaders, columnHeaders } = structure;
      const { cells } = crosstabData;

      // カラムスキーマを構築
      const columns: ColumnSchema[] = [];

      // 既存のスキーマがある場合は、そのカラムIDを使用
      if (existingSchema && existingSchema.columns.length > 0) {
        logger.info('[executeOperation] Using existing schema column IDs');

        // 既存のスキーマをコピー
        columns.push(...existingSchema.columns);
      } else {
        // 新規スキーマを生成
        logger.info('[executeOperation] Generating new schema');

        // Row Fieldsをカラムとして追加
        configuration.rowFields.forEach((rf, index) => {
          columns.push({
            id: createColumnId(`row_${index}_${rf.fieldName}`),
            name: createColumnName(rf.fieldName),
            type: rf.dataType,
            nullable: false
          });
        });

        // 各Column Headerをカラムとして追加
        columnHeaders.forEach((colHeader, colIndex) => {
          const colLabel = colHeader.displayValues.join(' / ');

          configuration.valueFields.forEach((vf, vfIndex) => {
            let columnName: string;
            if (configuration.valueFields.length === 1) {
              // 値フィールドが1つの場合は列名のみ
              columnName = colLabel;
            } else {
              // 複数の値フィールドがある場合は「列名_集計関数」
              const aggName = vf.alias || vf.aggregation.toUpperCase();
              columnName = `${colLabel}_${aggName}`;
            }

            columns.push({
              id: createColumnId(`col_${colIndex}_${vfIndex}`),
              name: createColumnName(columnName),
              type: 'float64',
              nullable: true
            });
          });
        });
      }

      // データを構築
      const columnData: Record<string, DataValue[]> = {};
      columns.forEach(col => {
        columnData[col.id] = [];
      });

      // 各行ヘッダーを1行として追加
      rowHeaders.forEach(rowHeader => {
        // Row Fieldsの値を追加
        const rowValues = JSON.parse(rowHeader.key) as (string | number | null)[];
        rowValues.forEach((val, index) => {
          const colId = columns[index].id;
          columnData[colId].push(val);
        });

        // 各列ヘッダーに対応するセルの値を追加
        const rowFieldCount = configuration.rowFields.length;
        columnHeaders.forEach((colHeader, colIndex) => {
          const cellKey = `row:${rowHeader.key}:col:${colHeader.key}`;
          const cellValue = cells.get(cellKey);

          configuration.valueFields.forEach((_, vfIndex) => {
            const colId = columns[rowFieldCount + colIndex * configuration.valueFields.length + vfIndex].id;

            if (cellValue && !cellValue.isEmpty) {
              columnData[colId].push(cellValue.values[vfIndex]);
            } else {
              columnData[colId].push(null);
            }
          });
        });
      });

      logger.info('[executeOperation] Crosstab executed successfully', {
        rowCount: rowHeaders.length,
        columnCount: columns.length
      });

      return {
        schema: { columns },
        data: columnData,
        rowCount: rowHeaders.length,
        parentDatasetIds: [operation.targetDatasetId]
      };
    }

    case 'filter': {
      // フィルタ操作の実行
      logger.info('[executeOperation] Executing filter operation', {
        targetDatasetId: operation.targetDatasetId
      });

      const targetDataset = datasets[operation.targetDatasetId];
      if (!targetDataset) {
        throw new Error(`Dataset ${operation.targetDatasetId} not found`);
      }

      // Ensure dataset has data
      let datasetWithData;
      if (!hasData(targetDataset)) {
        if (targetDataset.type === 'derived') {
          datasetWithData = await evaluateDerivedDataset(targetDataset, datasets);
        } else {
          throw new Error('Dataset does not have data loaded');
        }
      } else {
        datasetWithData = targetDataset;
      }

      // フィルタ条件を満たす行のインデックスを収集
      const filteredIndices: number[] = [];
      const rowCount = datasetWithData.rowCount || 0;

      for (let i = 0; i < rowCount; i++) {
        // 各行のデータを取得
        const rowData: Record<string, DataValue> = {};
        datasetWithData.schema.columns.forEach(col => {
          const columnData = datasetWithData.data![col.id];
          rowData[col.name] = columnData[i];
        });

        // FilterExpressionを評価
        if (FilterExpressionParser.evaluate(operation.filterExpression, rowData)) {
          filteredIndices.push(i);
        }
      }

      // フィルタ済みデータを構築
      const filteredData: Record<string, DataValue[]> = {};
      datasetWithData.schema.columns.forEach(col => {
        const sourceData = datasetWithData.data![col.id];
        filteredData[col.id] = filteredIndices.map(i => sourceData[i]);
      });

      logger.info('[executeOperation] Filter executed successfully', {
        originalRowCount: datasetWithData.rowCount,
        filteredRowCount: filteredIndices.length
      });

      return {
        schema: datasetWithData.schema,
        data: filteredData,
        rowCount: filteredIndices.length,
        parentDatasetIds: [operation.targetDatasetId]
      };
    }

    case 'glm_diagnostics': {
      // GLM診断データセットの再計算
      logger.info('[executeOperation] Executing GLM diagnostics operation', {
        modelId: operation.modelId
      });

      // ストアからmodelsを取得
      const models = useProjectStore.getState().project?.models;
      if (!models) {
        throw new Error('Project models not found in store');
      }

      const model = models[operation.modelId as import('../../types').ModelId];
      if (!model) {
        throw new Error(`Model ${operation.modelId} not found`);
      }

      logger.info('[executeOperation] Model details', {
        modelId: model.id,
        features: model.metadata.features,
        target: model.metadata.target
      });

      if (model.type !== 'glm') {
        throw new Error(`Model ${operation.modelId} is not a GLM model`);
      }

      // 親データセット（元のデータセット）を取得
      if (!parentDatasetId) {
        throw new Error('Parent dataset ID is required for GLM diagnostics operation');
      }

      const originalDataset = datasets[parentDatasetId];
      if (!originalDataset) {
        throw new Error(`Parent dataset ${parentDatasetId} not found`);
      }

      if (originalDataset.type !== 'primary') {
        throw new Error('GLM diagnostics requires a primary dataset');
      }

      logger.info('[executeOperation] Original dataset', {
        datasetId: originalDataset.id,
        columnCount: originalDataset.schema.columns.length,
        columnIds: originalDataset.schema.columns.map(c => c.id),
        columnNames: originalDataset.schema.columns.map(c => c.name)
      });

      // モデルパラメータと元データから診断統計量を完全再計算
      const { fitGLM } = await import('./glm');
      const { createDiagnosticDataset } = await import('./glm-diagnostics');
      const { DatasetAccessor } = await import('./DatasetAccessor');

      const parameters = model.parameters as import('../../types/model.types').GLMParameters;
      const accessor = new DatasetAccessor(originalDataset);
      const features = model.metadata.features;
      const target = model.metadata.target;

      // X行列とyベクトルを構築
      const X: number[][] = [];
      const y: number[] = [];
      const rowCount = originalDataset.rowCount;

      for (let i = 0; i < rowCount; i++) {
        const row: number[] = [];
        let hasInvalid = false;

        // 説明変数を取得（featuresは列ID）
        for (const featureId of features) {
          const col = originalDataset.schema.columns.find(c => c.id === featureId);
          if (!col) {
            throw new Error(`Feature column ${featureId} not found in dataset`);
          }
          const val = accessor.getValue(i, col.name) as number | null;

          if (val === null || val === undefined || !isFinite(val)) {
            hasInvalid = true;
            break;
          }
          row.push(val);
        }

        // 応答変数を取得（targetは列ID）
        const targetCol = originalDataset.schema.columns.find(c => c.id === target);
        if (!targetCol) {
          throw new Error(`Target column ${target} not found in dataset`);
        }
        const yVal = accessor.getValue(i, targetCol.name) as number | null;

        if (yVal === null || yVal === undefined || !isFinite(yVal)) {
          hasInvalid = true;
        }

        // 有効な観測のみを追加
        if (!hasInvalid) {
          X.push(row);
          y.push(yVal!);
        }
      }

      // fitGLMを実行（診断統計量を再計算）
      const result = fitGLM(X, y, parameters.family, parameters.link, {
        includeIntercept: true,
        maxIterations: 100,
        tolerance: 1e-8
      });

      // 診断データセットを再構築
      const diagnosticDataset = await createDiagnosticDataset(originalDataset, model, result.diagnostics);

      return {
        schema: diagnosticDataset.schema,
        data: diagnosticDataset.data!,
        rowCount: diagnosticDataset.rowCount || 0,
        parentDatasetIds: diagnosticDataset.parentDatasetIds
      };
    }

    case 'glm_prediction': {
      // GLM予測の実行
      logger.info('[executeOperation] Executing GLM prediction operation', {
        modelId: operation.modelId,
        targetDatasetId: operation.targetDatasetId
      });

      // ストアからmodelsを取得
      const models = useProjectStore.getState().project?.models;
      if (!models) {
        throw new Error('Project models not found in store');
      }

      const model = models[operation.modelId as import('../../types').ModelId];
      if (!model) {
        throw new Error(`Model ${operation.modelId} not found`);
      }

      if (model.type !== 'glm') {
        throw new Error(`Model ${operation.modelId} is not a GLM model`);
      }

      const targetDataset = datasets[operation.targetDatasetId];
      if (!targetDataset) {
        throw new Error(`Target dataset ${operation.targetDatasetId} not found`);
      }

      // トレーニングデータセットを取得
      const trainingDataset = datasets[model.metadata.trainingDatasetId];
      if (!trainingDataset) {
        throw new Error(`Training dataset ${model.metadata.trainingDatasetId} not found`);
      }

      // 予測を実行
      const { predictGLM } = await import('./glm-prediction');

      const predictionDataset = predictGLM(model, targetDataset, trainingDataset, {
        includeOriginalData: true,  // executeOperationでは常に元データを含める
        confidenceLevels: operation.confidenceLevels,
        predictionLevels: operation.predictionLevels
      });

      return {
        schema: predictionDataset.schema,
        data: predictionDataset.data!,
        rowCount: predictionDataset.rowCount || 0,
        parentDatasetIds: predictionDataset.parentDatasetIds
      };
    }

    case 'dummy_coding': {
      // Dummy Coding操作の実行
      logger.info('[executeOperation] Executing dummy coding operation', {
        targetDatasetId: operation.targetDatasetId,
        encodingCount: operation.encodings.length
      });

      const targetDataset = datasets[operation.targetDatasetId];
      if (!targetDataset) {
        throw new Error(`Dataset ${operation.targetDatasetId} not found`);
      }

      // Ensure dataset has data
      let datasetWithData;
      if (!hasData(targetDataset)) {
        if (targetDataset.type === 'derived') {
          datasetWithData = await evaluateDerivedDataset(targetDataset, datasets);
        } else {
          throw new Error('Dataset does not have data loaded');
        }
      } else {
        datasetWithData = targetDataset;
      }

      // encodingsから、ダミー変数化する列を特定
      const columnsToEncode = new Set<ColumnId>(
        operation.encodings.map(enc => enc.originalColumnId as ColumnId)
      );

      // 全列をcolumnIdsとして使用
      const columnIds = datasetWithData.schema.columns.map(col => col.id);

      // createDummyCodedDatasetを呼び出す
      // 注意: columnsToKeepOriginalの情報はencodingsに含まれていないため、
      // 再評価時は「元の列を残さない」動作になる（Issue #172で解決予定）
      const { createDummyCodedDataset } = await import('./create-dummy-coded-dataset');
      const result = createDummyCodedDataset(
        datasetWithData,
        columnIds,
        columnsToEncode
      );

      logger.info('[executeOperation] Dummy coding executed successfully', {
        originalColumnCount: columnIds.length,
        newColumnCount: result.dataset.schema.columns.length,
        rowCount: result.dataset.rowCount
      });

      return {
        schema: result.dataset.schema,
        data: result.dataset.data!,
        rowCount: result.dataset.rowCount || 0,
        parentDatasetIds: [operation.targetDatasetId]
      };
    }

    default: {
      // TypeScriptの網羅性チェック
      // never型を使用することで、すべてのケースが処理されていることを保証する
      // もし新しい操作タイプが追加されてここに到達する場合、TypeScriptがエラーを出す
      const exhaustiveCheck: never = operation;
      // @ts-expect-error - 実行時に未知の操作タイプが渡された場合のエラーメッセージ生成
      throw new Error(`Unknown operation type: ${exhaustiveCheck.type}`);
    }
  }
}

/**
 * 操作を実行して結果を返す（WithoutLock版 - Worker内からの呼び出し用）
 *
 * **依存性注入パターン**：
 * SQL実行関数（sqlExecutor）を引数として受け取ることで、
 * 呼び出し元が実行環境に適した実装を選択できます。
 * - メインスレッド: duckdbProxy.executeSQL（新しいWorkerを起動）
 * - Worker内: DuckDBQueryEngine.executeSQLWithoutLock（直接実行）
 *
 * これにより、Worker内から呼ばれた時に新しいWorkerを作成してしまう
 * 問題を回避できます。
 *
 * **処理フロー**：
 * - sql_query: 注入されたsqlExecutorを呼び出す
 * - filter: フィルタ条件を満たす行をメモリ上で抽出する
 * - crosstab: クロス集計をメモリ上で実行する
 * - glm_diagnostics: GLM診断統計量をメモリ上で計算する
 *
 * **使用場面**：
 * - DuckDBQueryEngine.ensureRequiredDatasetsRegisteredByTableNames内から呼ばれる場合
 *   （Worker内でのDerivedDataSet評価時）
 *
 * @param operation 実行する操作
 * @param datasets 利用可能なデータセットのマップ
 * @param sqlExecutor SQL実行関数（環境に応じた実装を注入）
 * @param parentDatasetId 親データセットID（operationに含まれていない場合に使用）
 * @param existingSchema 既存のスキーマ（列IDを保持するため）
 * @param derivedDataset 再評価対象のDerivedDataSet（exclusionsを適用するため）
 * @returns 実行結果
 */
export async function executeOperationWithoutLock(
  operation: DataTransformOperationUnion,
  datasets: Record<string, DataSet>,
  sqlExecutor: SqlExecutor,
  parentDatasetId?: string,
  existingSchema?: DataSchema,
  derivedDataset?: DerivedDataSet
): Promise<OperationResult> {
  switch (operation.type) {
    case 'sql_query': {
      // SQLクエリの実行（注入されたSQL実行関数を使用）
      const targetDataset = datasets[operation.targetDatasetId];
      if (!targetDataset) {
        throw new Error(`Dataset ${operation.targetDatasetId} not found`);
      }

      // 注入されたSQL実行関数を呼び出す
      const result = await sqlExecutor(operation.query, targetDataset, datasets, existingSchema);

      // parentDatasetIdsを取得（DerivedDataSetから、または操作のtargetから）
      const parentDatasetIds = derivedDataset?.parentDatasetIds || [operation.targetDatasetId];

      // DerivedDataSetのexclusionsを適用
      if (derivedDataset?.exclusions?.excludedRows && Object.keys(derivedDataset.exclusions.excludedRows).length > 0) {
        logger.info('[executeOperationWithoutLock] Applying exclusions to SQL query result', {
          excludedRowCount: Object.keys(derivedDataset.exclusions.excludedRows).length
        });

        // Row #列を名前で検索
        const rowIdColumnDef = result.schema.columns.find(col => col.name === ROW_ID_COLUMN_NAME);
        if (!rowIdColumnDef) {
          logger.warn('[executeOperationWithoutLock] Row ID column not found in result schema, cannot apply exclusions', {
            availableColumns: result.schema.columns.map(c => c.name)
          });
          return { ...result, parentDatasetIds };
        }

        const rowIdData = result.data[rowIdColumnDef.id];
        if (!rowIdData) {
          logger.warn('[executeOperationWithoutLock] Row ID column data not found, cannot apply exclusions', {
            columnId: rowIdColumnDef.id,
            dataKeys: Object.keys(result.data)
          });
          return { ...result, parentDatasetIds };
        }

        // 除外対象のRowNumber値のセット
        const excludeRowIdsSet = new Set<number>(Object.keys(derivedDataset.exclusions.excludedRows).map(Number));

        // 除外対象でない行のインデックスを収集
        const validIndices: number[] = [];
        for (let i = 0; i < result.rowCount; i++) {
          const rowIdValue = rowIdData[i];
          if (typeof rowIdValue === 'number' && !excludeRowIdsSet.has(rowIdValue)) {
            validIndices.push(i);
          }
        }

        // フィルタ済みデータを構築
        const filteredData: Record<string, DataValue[]> = {};
        result.schema.columns.forEach(col => {
          const colData = result.data[col.id];
          if (colData) {
            filteredData[col.id] = validIndices.map(i => colData[i]);
          }
        });

        const excludedCount = result.rowCount - validIndices.length;
        logger.info('[executeOperationWithoutLock] Excluded rows from result', {
          originalRowCount: result.rowCount,
          excludedCount,
          remainingRows: validIndices.length
        });

        return {
          ...result,
          data: filteredData,
          rowCount: validIndices.length,
          parentDatasetIds
        };
      }

      return { ...result, parentDatasetIds };
    }

    // 以下の操作はロック不要なので、Public版と同じ実装
    case 'crosstab':
    case 'filter':
    case 'glm_diagnostics':
    case 'glm_prediction':
    case 'dummy_coding':
      return executeOperation(operation, datasets, parentDatasetId, existingSchema, derivedDataset);

    default: {
      const exhaustiveCheck: never = operation;
      // @ts-expect-error - 実行時に未知の操作タイプが渡された場合のエラーメッセージ生成
      throw new Error(`Unknown operation type: ${exhaustiveCheck.type}`);
    }
  }
}